// ─── Workshop Truck Availability Tracker ───
// Toast – Notification banners (success / error / warning)
// IASL Technical Academy / Lafarge Driving Institute, Nigeria

import { useEffect } from 'react'
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react'
import type { ToastMessage } from '@/types'

interface ToastContainerProps {
  toasts: ToastMessage[]
  onDismiss: (id: string) => void
}

const toastConfig: Record<
  ToastMessage['type'],
  { bg: string; Icon: typeof CheckCircle }
> = {
  success: { bg: 'bg-green-600',  Icon: CheckCircle },
  error:   { bg: 'bg-red-600',    Icon: XCircle },
  warning: { bg: 'bg-amber-500',  Icon: AlertTriangle },
}

function SingleToast({
  toast,
  onDismiss,
}: {
  toast: ToastMessage
  onDismiss: (id: string) => void
}) {
  const { bg, Icon } = toastConfig[toast.type]

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), 4000)
    return () => clearTimeout(timer)
  }, [toast.id, onDismiss])

  return (
    <div
      className={`${bg} text-white rounded-lg px-4 py-3 shadow-lg
                  flex items-center gap-3 min-w-[300px] max-w-md
                  animate-slide-in`}
      role="alert"
    >
      <Icon size={18} className="flex-shrink-0" />
      <span className="text-sm flex-1">{toast.message}</span>
      <button
        onClick={() => onDismiss(toast.id)}
        className="ml-auto text-white/70 hover:text-white text-lg font-bold
                   cursor-pointer leading-none"
      >
        ×
      </button>
    </div>
  )
}

export default function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
      {toasts.map((t) => (
        <SingleToast key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  )
}
